import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
  type MRT_ColumnDef,
} from 'material-react-table';

import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
  lighten,
} from '@mui/material';

import { MembersRegistryTableData } from '../../../interface/IMembersRegistry';

import helper from '../../../helpers/index';


import iconEdit from '../../../assets/edit-icon.png';
import iconDelete from '../../../assets/delete.png';
import iconView from '../../../assets/visible.png';



//example data type


const columnHelper = createMRTColumnHelper<MembersRegistryTableData>();

interface AdminRegistryTableProps {
  data: MembersRegistryTableData[];
  deleteMemberButtonClick: (memberId: number, name: string) => void;
  isReadOnly: boolean;
  showActiveOnly: boolean;
  onToggleActiveOnly: (value: boolean) => void;
}


const AdminRegistryTable: React.FC<AdminRegistryTableProps> = ({ data, deleteMemberButtonClick, isReadOnly, showActiveOnly, onToggleActiveOnly }) => {

  const navigate = useNavigate();

  function UpdateMemberButtonClick(memberId: number) {
    navigate('/Admin/UpdateMember/' + memberId);

  }

  function ViewMemberButtonClick(memberId: number) {
    navigate('/Admin/ViewMember/' + memberId);
  }

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<MembersRegistryTableData>[]>(
    () => [
      {
        accessorFn: (row: MembersRegistryTableData) => `${row.firstname} ${row.lastname}`,
        id: 'fullName',
        header: 'Full Name',
        enableSorting: true,
        enableFiltering: true
      },
      {
        // accessorKey: 'membershipId',
        accessorFn: (row: MembersRegistryTableData) => `#${row.membershipId}`,
        header: 'Member ID',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.membershipId;
          const idB = rowB.original.membershipId;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        // accessorKey: 'dateAccepted',
        accessorFn: (row: MembersRegistryTableData) => {

          if (row.dateAccepted) {
            return `${helper.formatLongDate(row.dateAccepted)}`
          }
          else {
            return "";

          }


        },
        header: 'Date Accepted',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const firstData = new Date(rowA.original.dateAccepted).getTime() || 0;
          const secondData = new Date(rowB.original.dateAccepted).getTime() || 0;
          // Perform numeric comparison
          return Number(firstData) - Number(secondData);
        }
      },
      {
        accessorKey: 'email',
        header: 'Email'
      },
      {
        // accessorKey: 'verificationStatus', 
        accessorFn: (row: MembersRegistryTableData) => {
          if (row.verificationStatus) {
            return <span style={{ fontWeight: 'bold', color: 'green' }}>Verified</span>;
          } else {
            return <span style={{ fontWeight: 'bold', color: 'orange' }}>Unverified</span>;
          }
        },
        header: 'Verification Status',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.verificationStatus;
          const idB = rowB.original.verificationStatus;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        accessorFn: (row: MembersRegistryTableData) => {
          if (row.accountStatus) {
            return <span style={{ fontWeight: 'bold', color: 'green' }}>Complete</span>;
          } else {
            return <span style={{ fontWeight: 'bold', color: 'red' }}>Incomplete</span>;
          }
        },
        header: 'Account Status',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.accountStatus;
          const idB = rowB.original.accountStatus;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        header: 'Actions',
        Cell: ({ renderedCellValue, row }) => (
          <Box className='table-btn'>
            <Tooltip title="Edit">
              <Button
                className="btn-icon-1"
                disabled={isReadOnly}
                onClick={() => UpdateMemberButtonClick(row.original.memberId)}
              >
                <img className='responsive-image' src={iconEdit} />
              </Button>
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                disabled={isReadOnly}
                className="btn-icon-1"
                onClick={() => deleteMemberButtonClick(row.original.memberId, row.original.firstname + " " + row.original.lastname)}
              >
                <img className='responsive-image' src={iconDelete} />
              </Button>
            </Tooltip>
            <Tooltip title="Member Overview">
              <Button
                className="btn-icon-1"
                onClick={() => ViewMemberButtonClick(row.original.memberId)}
              >
                <img className='responsive-image' src={iconView} />
              </Button>
            </Tooltip>
          </Box>
        )
      }
    ],
    [isReadOnly],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    muiTablePaperProps: {
      elevation: 1,
      sx: {
        boxShadow: 'none',
        width: '100%',
        background: 'transparent',
        display: 'grid',

      }
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: showActiveOnly ? '#2e7d32' : '#6b7280', transition: 'color 0.2s', userSelect: 'none' }}>
          Show Active Only
        </Typography>
        <Box
          onClick={() => onToggleActiveOnly(!showActiveOnly)}
          sx={{
            position: 'relative',
            width: '46px',
            height: '24px',
            borderRadius: '999px',
            backgroundColor: showActiveOnly ? '#4caf50' : '#d1d5db',
            cursor: 'pointer',
            transition: 'background-color 0.25s ease',
            boxShadow: showActiveOnly ? '0 0 0 3px rgba(76,175,80,0.2)' : 'none',
            flexShrink: 0,
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: '3px',
            left: showActiveOnly ? '25px' : '3px',
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            transition: 'left 0.25s ease',
          }} />
        </Box>
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default AdminRegistryTable;
